const AIRWALLEX_POLL_INTERVAL_MS = 2000;
const AIRWALLEX_MAX_POLL_ATTEMPTS = 30;
const AIRWALLEX_DIRECT_TOPUP_STORAGE_PREFIX = 'airwallex_direct_topup_payment';
const AIRWALLEX_DIRECT_TOPUP_SCHEMA_VERSION = 2;
const AIRWALLEX_DIRECT_TOPUP_IDLE_TTL_MS = 2 * 60 * 60 * 1000;
const AIRWALLEX_DIRECT_TOPUP_MAX_LIFETIME_MS = 24 * 60 * 60 * 1000;
const AIRWALLEX_DIRECT_TOPUP_MAX_FUTURE_SKEW_MS = 5 * 60 * 1000;
const AIRWALLEX_OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{22}$/;
const SUPPORT_REFERENCE_PATTERN = /^CC1-(SGD|USD|AUD)-[0-9A-Z]+-[0-9A-F]{6}$/;
const PAYMENT_GATEWAY_STAFF_ROLES = ['Admin', 'Agent'];
const clearedAirwallexDirectTopupKeys = new Set();

export function toCreditCardPaymentMessage(message) {
  if (typeof message !== 'string') {
    return message;
  }

  return message
    .replace(/\ban airwallex\b/gi, (match) => (
      match.charAt(0) === 'A' ? 'A credit card' : 'a credit card'
    ))
    .replace(/airwallex/gi, 'credit card');
}

export function toPaymentAlertMessage(message, role) {
  if (PAYMENT_GATEWAY_STAFF_ROLES.includes(role)) {
    return message;
  }

  return toCreditCardPaymentMessage(message);
}

export function creditCardPaymentMethodLabel(paymentMethod) {
  const method = paymentMethod || {};

  if (Number(method.id) === 8 || Number(method.value) === 8) return 'Apple Pay';

  if (Number(method.id) === 7 || Number(method.value) === 7) {
    return 'Credit Card';
  }

  return toCreditCardPaymentMessage(method.text);
}

export function airwallexCustomerErrorMessage(
  error,
  fallback = 'The credit card request could not be completed.',
) {
  const responseData = error && error.response
    ? error.response.data
    : null;
  const responseMessage = responseData && typeof responseData === 'object'
    ? responseData.message
    : responseData;
  const message = responseMessage
    || (error && error.message)
    || fallback;

  return toCreditCardPaymentMessage(message);
}

function requiredAirwallexResponseValue(value, message) {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(message);
  }

  return value.trim();
}

export async function saveAirwallexCardConsent(
  api,
  airwallexInterface,
  requestContext = {},
) {
  if (!api || typeof api.post !== 'function'
    || !airwallexInterface
    || typeof airwallexInterface.createConsent !== 'function'
    || !requestContext
    || typeof requestContext !== 'object'
    || Array.isArray(requestContext)
  ) {
    throw new Error('The credit card payment form is not ready.');
  }

  // This is one logical, multi-request operation. Suppress interceptor alerts
  // so the calling view can emit one customer-safe result for API or SDK errors.
  const setupResponse = await api.post('partner/payment/save-card', {
    ...requestContext,
    payment_type_id: 7,
  }, {
    suppressErrorAlert: true,
  });
  const setup = setupResponse && setupResponse.data
    && typeof setupResponse.data === 'object'
    ? setupResponse.data
    : {};
  const customerId = requiredAirwallexResponseValue(
    setup.customer_id,
    'The credit card customer details are missing.',
  );
  const clientSecret = requiredAirwallexResponseValue(
    setup.client_secret,
    'The credit card authorization details are missing.',
  );
  const currency = requiredAirwallexResponseValue(
    setup.currency,
    'The credit card currency is missing.',
  );
  const consent = await airwallexInterface.createConsent(
    customerId,
    clientSecret,
    currency,
  );
  const consentId = requiredAirwallexResponseValue(
    consent && (consent.payment_consent_id || consent.id),
    'The credit card consent could not be verified.',
  );

  return api.post('partner/payment/save-card', {
    ...requestContext,
    payment_type_id: 7,
    confirm: 1,
    id: consentId,
  }, {
    suppressErrorAlert: true,
  });
}

function encodeOperationId(bytes) {
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return window.btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function directTopupRecoveryError() {
  return new Error(
    'Secure payment recovery storage is unavailable. Please enable browser storage and try again.',
  );
}

function normalizeAirwallexSupportReference(value, invalidMessage) {
  if (value === null || typeof value === 'undefined') {
    return null;
  }

  if (typeof value !== 'string') {
    throw new Error(invalidMessage);
  }

  const supportReference = value.trim();

  if (!SUPPORT_REFERENCE_PATTERN.test(supportReference)) {
    throw new Error(invalidMessage);
  }

  return supportReference;
}

function normalizeStoredAirwallexSupportReference(value) {
  try {
    return normalizeAirwallexSupportReference(
      value,
      directTopupRecoveryError().message,
    );
  } catch (error) {
    throw directTopupRecoveryError();
  }
}

function supportReferenceMatchesCurrency(reference, currency) {
  return reference === null || reference.split('-')[1] === currency;
}

export function creditCardPaymentSupportReferenceSuffix(reference) {
  try {
    const supportReference = normalizeAirwallexSupportReference(
      reference,
      'The credit card payment support reference is invalid.',
    );

    return supportReference ? ` Reference: ${supportReference}.` : '';
  } catch (error) {
    return '';
  }
}

export function createAirwallexPaymentOperationId() {
  const bytes = new Uint8Array(16);

  if (!window.crypto
    || typeof window.crypto.getRandomValues !== 'function'
  ) {
    throw directTopupRecoveryError();
  }

  window.crypto.getRandomValues(bytes);

  return encodeOperationId(bytes);
}

function normalizeTopupAmount(amount) {
  const stringAmount = String(amount).trim();

  if (!/^\d+(?:\.\d{1,2})?$/.test(stringAmount)) {
    throw directTopupRecoveryError();
  }

  const amountParts = stringAmount.split('.');
  const whole = amountParts[0].replace(/^0+(?=\d)/, '');
  const fraction = (amountParts[1] || '').padEnd(2, '0');

  if (whole === '0' && fraction === '00') {
    throw directTopupRecoveryError();
  }

  return `${whole}.${fraction}`;
}

function normalizeDirectTopupIdentifier(value) {
  const identifier = value === null || typeof value === 'undefined'
    ? ''
    : String(value).trim();

  if (!/^[A-Za-z0-9_-]{1,128}$/.test(identifier)
    || identifier === '0'
    || identifier === 'anonymous'
    || identifier === 'unknown'
  ) {
    throw directTopupRecoveryError();
  }

  return identifier;
}

function normalizeAirwallexDirectTopupContext(context) {
  if (!context || typeof context !== 'object' || Array.isArray(context)) {
    throw directTopupRecoveryError();
  }

  const brand = String(context.brand || '').trim().toLowerCase();
  const currency = String(context.currency || '').trim().toUpperCase();
  const environment = String(context.environment || '').trim().toLowerCase();
  const accountScope = String(context.accountScope || '').trim().toLowerCase();
  const mode = String(context.mode || '').trim().toLowerCase();
  const userId = normalizeDirectTopupIdentifier(context.userId);
  const cardId = mode !== 'saved'
    ? 'new'
    : normalizeDirectTopupIdentifier(context.cardId);

  if (!/^[a-z0-9_-]{1,64}$/.test(brand)
    || !/^[A-Z]{3}$/.test(currency)
    || !['demo', 'prod'].includes(environment)
    || !['gce', 'cldy'].includes(accountScope)
    || !['new', 'saved', 'apple_pay'].includes(mode)
    || (mode === 'saved' && cardId === 'new')
  ) {
    throw directTopupRecoveryError();
  }

  return {
    brand,
    currency,
    environment,
    account_scope: accountScope,
    user_id: userId,
    mode,
    card_id: cardId,
    amount: normalizeTopupAmount(context.amount),
  };
}

function directTopupStorageKey(normalizedContext) {
  const parts = [
    `v${AIRWALLEX_DIRECT_TOPUP_SCHEMA_VERSION}`,
    normalizedContext.brand,
    normalizedContext.currency,
    normalizedContext.environment,
    normalizedContext.account_scope,
    normalizedContext.user_id,
    normalizedContext.mode,
    normalizedContext.card_id,
    normalizedContext.amount,
  ];

  return `${AIRWALLEX_DIRECT_TOPUP_STORAGE_PREFIX}:${parts
    .map((part) => encodeURIComponent(String(part)))
    .join(':')}`;
}

function getLocalStorage() {
  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
}

function normalizePaymentIntentId(value) {
  if (value === null || typeof value === 'undefined') {
    return null;
  }

  if (typeof value !== 'string') {
    throw directTopupRecoveryError();
  }

  const paymentIntentId = value.trim();

  if (!paymentIntentId || paymentIntentId.length > 255) {
    throw directTopupRecoveryError();
  }

  return paymentIntentId;
}

function sameDirectTopupContext(first, second) {
  return first.brand === second.brand
    && first.currency === second.currency
    && first.environment === second.environment
    && first.account_scope === second.account_scope
    && first.user_id === second.user_id
    && first.mode === second.mode
    && first.card_id === second.card_id
    && first.amount === second.amount;
}

function normalizeStoredDirectTopup(record, now) {
  if (!record || typeof record !== 'object' || Array.isArray(record)
    || record.schema_version !== AIRWALLEX_DIRECT_TOPUP_SCHEMA_VERSION
    || !record.context
    || typeof record.context !== 'object'
    || Array.isArray(record.context)
    || typeof record.payment_operation_id !== 'string'
    || !AIRWALLEX_OPERATION_ID_PATTERN.test(record.payment_operation_id)
    || (record.payment_intent_id !== null
      && typeof record.payment_intent_id !== 'string')
    || typeof record.browser_confirmed !== 'boolean'
    || (typeof record.completed !== 'undefined'
      && typeof record.completed !== 'boolean')
    || !Number.isInteger(record.created_at)
    || !Number.isInteger(record.updated_at)
    || record.created_at > record.updated_at
    || record.created_at > now + AIRWALLEX_DIRECT_TOPUP_MAX_FUTURE_SKEW_MS
    || record.updated_at > now + AIRWALLEX_DIRECT_TOPUP_MAX_FUTURE_SKEW_MS
    || now - record.updated_at > AIRWALLEX_DIRECT_TOPUP_IDLE_TTL_MS
    || now - record.created_at > AIRWALLEX_DIRECT_TOPUP_MAX_LIFETIME_MS
  ) {
    throw directTopupRecoveryError();
  }

  const normalizedContext = normalizeAirwallexDirectTopupContext({
    brand: record.context.brand,
    currency: record.context.currency,
    environment: record.context.environment,
    accountScope: record.context.account_scope,
    userId: record.context.user_id,
    mode: record.context.mode,
    cardId: record.context.card_id,
    amount: record.context.amount,
  });
  const paymentIntentId = normalizePaymentIntentId(record.payment_intent_id);
  const supportReference = normalizeStoredAirwallexSupportReference(
    record.support_reference,
  );
  const browserConfirmed = record.browser_confirmed === true;
  const completed = record.completed === true;

  if (!sameDirectTopupContext(normalizedContext, record.context)
    || !supportReferenceMatchesCurrency(
      supportReference,
      normalizedContext.currency,
    )
    || (browserConfirmed && !paymentIntentId)
    || (completed && (!browserConfirmed || !paymentIntentId))
  ) {
    throw directTopupRecoveryError();
  }

  return {
    schema_version: AIRWALLEX_DIRECT_TOPUP_SCHEMA_VERSION,
    context: normalizedContext,
    payment_operation_id: record.payment_operation_id,
    payment_intent_id: paymentIntentId,
    support_reference: supportReference,
    browser_confirmed: browserConfirmed,
    completed,
    created_at: record.created_at,
    updated_at: record.updated_at,
  };
}

function removeDirectTopupStorageItem(storage, storageKey) {
  try {
    storage.removeItem(storageKey);
  } catch (error) {
    // Reads and writes will fail closed when storage cannot be updated.
  }
}

function cleanupAirwallexDirectTopups(storage, now) {
  const storageKeys = [];

  for (let index = 0; index < storage.length; index += 1) {
    const storageKey = storage.key(index);

    if (typeof storageKey === 'string'
      && storageKey.indexOf(`${AIRWALLEX_DIRECT_TOPUP_STORAGE_PREFIX}:`) === 0
    ) {
      storageKeys.push(storageKey);
    }
  }

  storageKeys.forEach((storageKey) => {
    try {
      const record = normalizeStoredDirectTopup(
        JSON.parse(storage.getItem(storageKey)),
        now,
      );

      if (directTopupStorageKey(record.context) !== storageKey) {
        removeDirectTopupStorageItem(storage, storageKey);
      }
    } catch (error) {
      removeDirectTopupStorageItem(storage, storageKey);
    }
  });
}

function directTopupOperation(record) {
  return {
    payment_operation_id: record.payment_operation_id,
    payment_intent_id: record.payment_intent_id,
    support_reference: record.support_reference,
    browser_confirmed: record.browser_confirmed,
    created_at: record.created_at,
    updated_at: record.updated_at,
  };
}

function readStoredDirectTopup(storage, storageKey, normalizedContext, now) {
  const storedValue = storage.getItem(storageKey);

  if (!storedValue) {
    return null;
  }

  try {
    const record = normalizeStoredDirectTopup(JSON.parse(storedValue), now);

    if (directTopupStorageKey(record.context) !== storageKey
      || !sameDirectTopupContext(record.context, normalizedContext)
    ) {
      throw directTopupRecoveryError();
    }

    return record;
  } catch (error) {
    removeDirectTopupStorageItem(storage, storageKey);
    return null;
  }
}

export function getAirwallexDirectTopup(context) {
  const storage = getLocalStorage();

  if (!storage) {
    return null;
  }

  try {
    const now = Date.now();
    const normalizedContext = normalizeAirwallexDirectTopupContext(context);
    const storageKey = directTopupStorageKey(normalizedContext);

    if (clearedAirwallexDirectTopupKeys.has(storageKey)) {
      removeDirectTopupStorageItem(storage, storageKey);
      return null;
    }

    cleanupAirwallexDirectTopups(storage, now);
    const record = readStoredDirectTopup(
      storage,
      storageKey,
      normalizedContext,
      now,
    );

    if (record && record.completed) {
      clearedAirwallexDirectTopupKeys.add(storageKey);
      removeDirectTopupStorageItem(storage, storageKey);
      return null;
    }

    return record ? directTopupOperation(record) : null;
  } catch (error) {
    return null;
  }
}

export function saveAirwallexDirectTopup(context, operation) {
  const storage = getLocalStorage();

  if (!storage || !operation || typeof operation !== 'object') {
    throw directTopupRecoveryError();
  }

  const normalizedContext = normalizeAirwallexDirectTopupContext(context);
  const storageKey = directTopupStorageKey(normalizedContext);
  const now = Date.now();

  if (typeof operation.payment_operation_id !== 'string'
    || !AIRWALLEX_OPERATION_ID_PATTERN.test(operation.payment_operation_id)
  ) {
    throw directTopupRecoveryError();
  }

  const paymentIntentId = normalizePaymentIntentId(operation.payment_intent_id);
  const hasSupportReference = Object.prototype.hasOwnProperty.call(
    operation,
    'support_reference',
  );
  const requestedSupportReference = hasSupportReference
    ? normalizeStoredAirwallexSupportReference(operation.support_reference)
    : null;
  const browserConfirmed = operation.browser_confirmed === true;

  if (!supportReferenceMatchesCurrency(
    requestedSupportReference,
    normalizedContext.currency,
  ) || (browserConfirmed && !paymentIntentId)) {
    throw directTopupRecoveryError();
  }

  try {
    cleanupAirwallexDirectTopups(storage, now);

    const existingRecord = readStoredDirectTopup(
      storage,
      storageKey,
      normalizedContext,
      now,
    );
    const sameOperation = existingRecord
      && existingRecord.payment_operation_id === operation.payment_operation_id;
    const createdAt = sameOperation
      ? existingRecord.created_at
      : now;
    const supportReference = hasSupportReference
      ? requestedSupportReference
      : (sameOperation && existingRecord.support_reference) || null;
    const record = {
      schema_version: AIRWALLEX_DIRECT_TOPUP_SCHEMA_VERSION,
      context: normalizedContext,
      payment_operation_id: operation.payment_operation_id,
      payment_intent_id: paymentIntentId,
      support_reference: supportReference,
      browser_confirmed: browserConfirmed,
      completed: false,
      created_at: createdAt,
      updated_at: now,
    };

    storage.setItem(storageKey, JSON.stringify(record));

    const persistedRecord = readStoredDirectTopup(
      storage,
      storageKey,
      normalizedContext,
      now,
    );

    if (!persistedRecord
      || JSON.stringify(persistedRecord) !== JSON.stringify(record)
    ) {
      throw directTopupRecoveryError();
    }

    clearedAirwallexDirectTopupKeys.delete(storageKey);
    return directTopupOperation(persistedRecord);
  } catch (error) {
    removeDirectTopupStorageItem(storage, storageKey);
    throw directTopupRecoveryError();
  }
}

export function getOrCreateAirwallexDirectTopup(context) {
  const storedOperation = getAirwallexDirectTopup(context);

  if (storedOperation) {
    return storedOperation;
  }

  return saveAirwallexDirectTopup(context, {
    payment_operation_id: createAirwallexPaymentOperationId(),
    payment_intent_id: null,
    browser_confirmed: false,
  });
}

export function clearAirwallexDirectTopup(
  context,
  completedOperation = null,
) {
  let storageKey;
  let normalizedContext;

  try {
    normalizedContext = normalizeAirwallexDirectTopupContext(context);
    storageKey = directTopupStorageKey(normalizedContext);
    clearedAirwallexDirectTopupKeys.add(storageKey);
  } catch (error) {
    return false;
  }

  const storage = getLocalStorage();

  if (!storage) {
    return false;
  }

  let tombstonePersisted = false;

  try {
    const now = Date.now();

    cleanupAirwallexDirectTopups(storage, now);

    if (completedOperation
      && typeof completedOperation === 'object'
      && typeof completedOperation.payment_operation_id === 'string'
      && AIRWALLEX_OPERATION_ID_PATTERN.test(
        completedOperation.payment_operation_id,
      )
    ) {
      const paymentIntentId = normalizePaymentIntentId(
        completedOperation.payment_intent_id,
      );
      const supportReference = normalizeStoredAirwallexSupportReference(
        completedOperation.support_reference,
      );
      const createdAt = Number.isInteger(completedOperation.created_at)
        ? completedOperation.created_at
        : now;
      const tombstone = {
        schema_version: AIRWALLEX_DIRECT_TOPUP_SCHEMA_VERSION,
        context: normalizedContext,
        payment_operation_id: completedOperation.payment_operation_id,
        payment_intent_id: paymentIntentId,
        support_reference: supportReference,
        browser_confirmed: true,
        completed: true,
        created_at: createdAt,
        updated_at: now,
      };

      storage.setItem(storageKey, JSON.stringify(tombstone));
      const persistedTombstone = readStoredDirectTopup(
        storage,
        storageKey,
        normalizedContext,
        now,
      );

      tombstonePersisted = !!persistedTombstone
        && persistedTombstone.completed === true;
    }
  } catch (error) {
    // Removal below can still succeed when storage writes are unavailable.
  }

  try {
    storage.removeItem(storageKey);

    if (storage.getItem(storageKey) === null) {
      return true;
    }
  } catch (error) {
    // A verified terminal tombstone is safe when removal is blocked.
  }

  return tombstonePersisted;
}

export function validateAirwallexDirectTopupResponse(
  response,
  responseType,
  expectedPaymentIntentId = null,
) {
  const responseData = response && response.data;

  if (!responseData
    || typeof responseData !== 'object'
    || Array.isArray(responseData)
    || (
      typeof responseData.payment_already_completed !== 'undefined'
      && typeof responseData.payment_already_completed !== 'boolean'
    )
  ) {
    throw new Error('The credit card processor returned an invalid response.');
  }

  let paymentIntentId;

  try {
    paymentIntentId = normalizePaymentIntentId(responseData.payment_intent_id);
  } catch (error) {
    paymentIntentId = null;
  }

  if (!paymentIntentId) {
    throw new Error('The credit card processor did not return payment details.');
  }

  const supportReference = normalizeAirwallexSupportReference(
    responseData.support_reference,
    'The credit card processor returned an invalid support reference.',
  );

  if (expectedPaymentIntentId !== null
    && paymentIntentId !== String(expectedPaymentIntentId).trim()
  ) {
    throw new Error(
      'The credit card processor returned payment details for a different payment.',
    );
  }

  if (responseData.payment_pending === true) {
    if (responseData.payment_completed === true
      || responseData.payment_already_completed === true
    ) {
      throw new Error('The credit card processor returned conflicting payment details.');
    }

    return {
      paymentIntentId,
      clientSecret: null,
      supportReference,
      pending: true,
      completed: false,
    };
  }

  if (responseType === 'authorization') {
    const clientSecret = typeof responseData.client_secret === 'string'
      ? responseData.client_secret.trim()
      : '';

    if (!clientSecret
      || responseData.payment_completed === true
      || responseData.payment_already_completed === true
    ) {
      throw new Error(
        'The credit card processor did not return authorization details.',
      );
    }

    return {
      paymentIntentId,
      clientSecret,
      supportReference,
      pending: false,
      completed: false,
    };
  }

  if (responseType !== 'completion'
    || responseData.payment_completed !== true
  ) {
    throw new Error(
      'The credit card processor did not confirm that the payment completed.',
    );
  }

  return {
    paymentIntentId,
    clientSecret: null,
    supportReference,
    pending: false,
    completed: true,
    alreadyCompleted: responseData.payment_already_completed === true,
  };
}

export function isAirwallexTerminalRequestError(error) {
  const status = error && error.response && error.response.status;

  return status === 400 || status === 402 || status === 422;
}

export function isValidAirwallexTopupAmount(amount) {
  const stringAmount = String(amount).trim();
  const numericAmount = Number(stringAmount);

  return /^\d+(?:\.\d{1,2})?$/.test(stringAmount)
    && Number.isFinite(numericAmount)
    && numericAmount > 0;
}

export function isAirwallexPaymentPending(response) {
  return !!(response && response.data && response.data.payment_pending);
}

function waitForNextPoll() {
  return new Promise((resolve) => {
    setTimeout(resolve, AIRWALLEX_POLL_INTERVAL_MS);
  });
}

export function pollAirwallexPayment(
  sendRequest,
  initialResponse = null,
  remainingAttempts = AIRWALLEX_MAX_POLL_ATTEMPTS,
  shouldContinue = () => true,
) {
  if (!shouldContinue()) {
    return Promise.resolve(null);
  }

  const responsePromise = initialResponse
    ? Promise.resolve(initialResponse)
    : Promise.resolve().then(() => {
      if (!shouldContinue()) {
        return null;
      }

      return sendRequest();
    });

  return responsePromise.then((response) => {
    if (!shouldContinue()) {
      return null;
    }

    if (!isAirwallexPaymentPending(response) || remainingAttempts <= 1) {
      return response;
    }

    return waitForNextPoll()
      .then(() => pollAirwallexPayment(
        sendRequest,
        null,
        remainingAttempts - 1,
        shouldContinue,
      ));
  }, (error) => {
    if (!shouldContinue()) {
      return null;
    }

    throw error;
  });
}
